import React, { useCallback, useRef, useMemo } from "react";
import clsx from "clsx";

import type {
    Column,
    ColumnState,
    FocusGridInnerElementParams,
    GetRowIdFunc,
    GridApi,
    GridProps,
    StateUpdatedEvent,
    CellKeyDownEvent,
    CellEditRequestEvent,
    ProcessCellForExportParams,
    GetContextMenuItemsParams,
    RangeSelectionChangedEvent,
    SelectionChangedEvent,
} from "@/ui/molecules/Grid";
import Grid from "@/ui/molecules/Grid";

import type { ExtractionsErrorListProps } from "@/ui/organisms/ExtractionsSidebar/ExtractionsErrorList";

import type {
    TAllRowsAvailable,
    TColumnDefsOperatingStatements,
    TOptionWithType,
    TRowCategory,
    TRowMappingsWithOperatingStatements,
    TRowTotal,
    TRowTotalPinned,
} from "../typesGrids";
import { makeGridMappingsColumnDefs } from "./makeGridMappingsColumnDefs";
import { isRowOmitted, isRowWithOperatingStatements } from "../typeChecksGrids";
import { formatNumber } from "../utils";
import { getGridMappingsContextMenuItems } from "./GridMappingsContextMenu";
import { parseEditedDollars } from "./parseEditedDollars";
import {
    isOmitOption,
    OMIT_ROW_OPTION,
} from "./GridMappingsDropdownItemAccounts";

type TRowType = "account" | "empty" | "category" | "total";
type TValueCellSelection = {
    rowId: number;
    columnId: number;
    current: number | null;
};

const isCellKeyDownEventTypeGuard = (
    value: unknown,
): value is CellKeyDownEvent<TAllRowsAvailable> =>
    typeof value === "object" &&
    value !== null &&
    "event" in value &&
    value.event instanceof KeyboardEvent;

export type GridMappingsProps = Omit<
    GridProps<TAllRowsAvailable>,
    "columnDefs" | "rowData" | "readOnlyEdit" | "onCellEditRequest"
> & {
    columnDefsOperatingStatements: TColumnDefsOperatingStatements;
    rowData: TRowMappingsWithOperatingStatements[];
    totalRows: TRowTotal[];
    categoryRows: TRowCategory[];
    optionsAccounts: TOptionWithType[];
    totalNetIncomeOriginal: TRowTotalPinned | null;
    totalNetIncomeAltered: TRowTotalPinned | null;
    alteredRowsIds: number[];
    errorGroups: ExtractionsErrorListProps["errors"];
    editMode: "off" | "rows" | "columns";
    extractionId: number;
    highlightedTargetRowIds?: Array<number | string>;
    allowValueEditing: boolean;

    setSelectedRows?: (rowIds: number[]) => void;
    selectedColumns: number[];
    onToggleColumnSelection: (columnId: number, checked: boolean) => void;
    onChangeAccounts: (
        changes: { rowId: number; accountCode: string | null }[],
    ) => void;
    onChangeRowType: (params: { rowId: number; rowType: TRowType }) => void;
    onFlipRowSign: (params: { rowId: number }) => void;
    onSetDocumentTargetRow: (params: { rowId: number | null }) => void;
    onCellValuesEdited: (
        cells: { rowId: number; columnId: number; amount: number | null }[],
    ) => void;
    onValueCellSelected?: (
        cell: { rowId: number; columnId: number; field: string } | null,
    ) => void;
    setColumnState: (
        extractionId: number,
        state: boolean,
        columnId?: number,
    ) => Promise<void>;
};

const stylesToForceFullWidthOfRichEditSelectDropdownsItems = {
    "& .ag-rich-select-row": {
        width: "100%",
        display: "block",
        paddingLeft: "0px",
        paddingRight: "0px",
    },
    "& .ag-rich-select-row-highlighted": {
        backgroundColor: "var(--proda-light-blue-100)",
    },
    "& .ag-rich-select-row .ag-react-container": {
        width: "100%",
    },
    "& .ag-rich-select-row-selected": {
        backgroundColor: "var(--proda-white-110)",
    },
};

const disableRowAnimationOnZooming = {
    "& .ag-row-animation .ag-row": {
        transition: "none",
    },
};

const testId = "GridMappings";
const GridMappings = React.memo((props: GridMappingsProps) => {
    const initialColumnState = useRef<ColumnState[]>();
    const isPasting = useRef(false);
    const pasteBuffer = useRef<{ rowId: number; accountCode: string | null }[]>(
        [],
    );
    const valuePasteBuffer = useRef<
        { rowId: number; columnId: number; amount: number | null }[]
    >([]);
    const pendingValueFillSelection = useRef<TValueCellSelection[]>([]);

    const {
        rowData,
        totalRows,
        categoryRows,
        alteredRowsIds,
        columnDefsOperatingStatements: columnDefsOperatingStatementsFromProps,
        optionsAccounts,
        totalNetIncomeOriginal,
        totalNetIncomeAltered,
        errorGroups,
        extractionId,
        editMode,
        setSelectedRows,
        selectedColumns,
        onToggleColumnSelection,
        onChangeAccounts,
        onChangeRowType,
        onFlipRowSign,
        onSetDocumentTargetRow,
        onCellValuesEdited,
        onValueCellSelected,
        allowValueEditing,
        setColumnState,
        highlightedTargetRowIds,
        ...otherProps
    } = props;
    const highlightedSet = useMemo(
        () => new Set<number | string>(highlightedTargetRowIds ?? []),
        [highlightedTargetRowIds],
    );

    const rowDataWithTotalRows = useMemo(() => {
        const merged = [...totalRows, ...categoryRows, ...rowData];
        return merged.toSorted((a, b) => Number(a.rowId) - Number(b.rowId));
    }, [totalRows, rowData, categoryRows]);

    const accountsNonOperating = useMemo(
        () => optionsAccounts.filter(option => option.type === "non-operating"),
        [optionsAccounts],
    );

    const optionsAccountsIncomesThenExpensesThenExcludedWithOmit = useMemo(
        () => [OMIT_ROW_OPTION, ...optionsAccounts],
        [optionsAccounts],
    );

    const longestOperatingStatementAndTotalsNumericValue = useMemo(
        () =>
            rowDataWithTotalRows.reduce((acc, row) => {
                const numericValues = Object.values(row).filter(
                    (v): v is number => typeof v === "number",
                );
                const longestValue = Math.max(
                    ...numericValues.map(v => formatNumber(v).length),
                );
                return Math.max(acc, longestValue);
            }, 0),
        [rowDataWithTotalRows],
    );

    const columnDefs = useMemo(
        () =>
            makeGridMappingsColumnDefs({
                alteredRowsIds,
                optionsAccounts:
                    optionsAccountsIncomesThenExpensesThenExcludedWithOmit,
                columnDefsOperatingStatementsFromProps,
                longestOperatingStatementAndTotalsNumericValue,
                errorGroups,
                initialState: initialColumnState.current,
                editMode,
                extractionId,
                allowValueEditing,
                setColumnState,
                selectedColumns,
                onToggleColumnSelection,
            }),
        [
            alteredRowsIds,
            columnDefsOperatingStatementsFromProps,
            optionsAccountsIncomesThenExpensesThenExcludedWithOmit,
            longestOperatingStatementAndTotalsNumericValue,
            errorGroups,
            extractionId,
            editMode,
            allowValueEditing,
            setColumnState,
            selectedColumns,
            onToggleColumnSelection,
        ],
    );

    const pinnedBottomRowData = useMemo(() => {
        if (totalNetIncomeOriginal === null && totalNetIncomeAltered === null) {
            return [];
        }

        const originalTotalName = totalNetIncomeOriginal?.["totalName"];
        const originalValues: TRowTotalPinned = Object.fromEntries(
            Object.entries(totalNetIncomeOriginal ?? {}).filter(
                ([key]) => key !== "totalName" && key !== "rowId",
            ),
        );
        const hasOriginalDocumentTarget = totalNetIncomeOriginal !== null;
        const valuesMatch =
            hasOriginalDocumentTarget &&
            totalNetIncomeAltered !== null &&
            columnDefsOperatingStatementsFromProps.every(({ field }) => {
                const originalValue = originalValues[field] ?? null;
                const alteredValue = totalNetIncomeAltered[field] ?? null;
                return originalValue === alteredValue;
            });
        return [
            totalNetIncomeAltered && {
                rowId: "row-altered",
                totalName: "Mapped Net Operating Income",
                pinnedRowColor: valuesMatch ? "green" : "blue",
                pinnedRowTooltip:
                    "Based on current mappings: Sum of Income accounts minus Expense accounts. Non-Operating accounts excluded",
                ...totalNetIncomeAltered,
            },
            {
                rowId: "row-original",
                totalName:
                    typeof originalTotalName === "string"
                        ? originalTotalName
                        : "Original Document - target not identified",
                pinnedRowColor: hasOriginalDocumentTarget ? "green" : "red",
                ...(!hasOriginalDocumentTarget
                    ? {
                          pinnedRowTooltip:
                              "Target row not identified in source document. Set a target row manually via right-click",
                      }
                    : {}),
                ...originalValues,
            },
        ].filter(Boolean);
    }, [
        columnDefsOperatingStatementsFromProps,
        totalNetIncomeOriginal,
        totalNetIncomeAltered,
    ]);

    const gridOptions = useMemo<GridMappingsProps["gridOptions"]>(
        () => ({
            rowSelection: "multiple",
            rowMultiSelectWithClick: true,
            suppressRowClickSelection: true,
        }),
        [],
    );

    const defaultColDef = useMemo<GridMappingsProps["defaultColDef"]>(
        () => ({
            suppressHeaderMenuButton: true,
            sortable: false,
        }),
        [],
    );

    const onStateUpdated = useCallback((params: StateUpdatedEvent) => {
        initialColumnState.current = params.api.getColumnState();
    }, []);

    const focusFirstCellInAccountColumn = useCallback(
        (params: FocusGridInnerElementParams<TAllRowsAvailable>) => {
            const column = params.api.getColumn("account") as Column;
            params.api.setFocusedCell(0, column, null);
            return true;
        },
        [],
    );

    const getRowId: GetRowIdFunc<TAllRowsAvailable> = useCallback(
        params => String(params.data.rowId),
        [],
    );

    const processCellForClipboard = useCallback(
        (params: ProcessCellForExportParams<TAllRowsAvailable>) => {
            const isAccountColumn =
                params.column.getColDef().field === "account";

            if (!isAccountColumn) {
                if (typeof params.value === "number") {
                    return (params.value / 100).toFixed(2);
                }
                return typeof params.value === "string" ? params.value : "";
            }

            const data = params.node?.data;
            const mappingType =
                data && "mappingType" in data ? data.mappingType : null;

            if (mappingType === "omit") {
                return OMIT_ROW_OPTION.label;
            }

            const code = typeof params.value === "string" ? params.value : "";
            if (!code) return "";

            const match = optionsAccounts.find(opt => opt.value === code);
            return match ? match.label : code;
        },
        [optionsAccounts],
    );

    const processCellFromClipboard = useCallback(
        (params: ProcessCellForExportParams<TAllRowsAvailable>) => {
            const value =
                typeof params.value === "string" ? params.value.trim() : "";
            if (!value) return value;
            if (value.toLowerCase() === OMIT_ROW_OPTION.label.toLowerCase()) {
                return OMIT_ROW_OPTION.value;
            }
            if (optionsAccounts.some(opt => opt.value === value)) return value;
            const match = optionsAccounts.find(
                opt => opt.label.trim().toLowerCase() === value.toLowerCase(),
            );
            return match ? match.value : value;
        },
        [optionsAccounts],
    );

    const collectSelectedValueCells = useCallback(
        (api: GridApi<TAllRowsAvailable>): TValueCellSelection[] => {
            const columnIdByField: Record<string, number> = Object.fromEntries(
                columnDefsOperatingStatementsFromProps
                    .filter(c => c.columnId != null && c.isDisabled !== true)
                    .map(c => [c.field, c.columnId as number]),
            );
            const candidates = (api.getCellRanges() ?? []).flatMap(range => {
                const start = range.startRow?.rowIndex ?? 0;
                const end = range.endRow?.rowIndex ?? start;
                const rowIndexes = Array.from(
                    { length: Math.abs(end - start) + 1 },
                    (_, k) => Math.min(start, end) + k,
                );
                return rowIndexes.flatMap(i => {
                    const data = api.getDisplayedRowAtIndex(i)?.data;
                    if (!data || data.rowType !== "account") return [];
                    return range.columns.flatMap(column => {
                        const field = column.getColId();
                        const columnId = columnIdByField[field];
                        if (columnId === undefined) return [];
                        const value = data[field];
                        return [
                            {
                                rowId: Number(data.rowId),
                                columnId,
                                current:
                                    typeof value === "number" ? value : null,
                            },
                        ];
                    });
                });
            });
            return Object.values(
                Object.fromEntries(
                    candidates.map(cell => [
                        `${cell.rowId}:${cell.columnId}`,
                        cell,
                    ]),
                ),
            );
        },
        [columnDefsOperatingStatementsFromProps],
    );

    const getValueCellSelectionForEdit = useCallback(
        ({
            api,
            rowId,
            columnId,
        }: {
            api: GridApi<TAllRowsAvailable>;
            rowId: number;
            columnId: number;
        }) => {
            const hasEditedCell = (cells: TValueCellSelection[]) =>
                cells.some(
                    cell => cell.rowId === rowId && cell.columnId === columnId,
                );
            const currentSelection = collectSelectedValueCells(api);
            if (
                currentSelection.length > 1 &&
                hasEditedCell(currentSelection)
            ) {
                pendingValueFillSelection.current = [];
                return currentSelection;
            }

            const pendingSelection = pendingValueFillSelection.current;
            pendingValueFillSelection.current = [];
            if (
                pendingSelection.length > 1 &&
                hasEditedCell(pendingSelection)
            ) {
                return pendingSelection;
            }

            return [];
        },
        [collectSelectedValueCells],
    );

    // Snapshot the fill target when the range is made: the grid collapses the
    // range before edit-start key events fire, so keydown is too late.
    const onRangeSelectionChanged = useCallback(
        (event: RangeSelectionChangedEvent<TAllRowsAvailable>) => {
            const selected = collectSelectedValueCells(event.api);
            if (selected.length > 1) {
                pendingValueFillSelection.current = selected;
            } else if (event.api.getEditingCells().length === 0) {
                // Collapsed outside an edit (click/arrow-key) — drop it.
                pendingValueFillSelection.current = [];
            }
        },
        [collectSelectedValueCells],
    );

    const getContextMenuItems = useCallback(
        (params: GetContextMenuItemsParams<TAllRowsAvailable>) =>
            getGridMappingsContextMenuItems(
                params,
                onChangeRowType,
                onFlipRowSign,
                onSetDocumentTargetRow,
            ),
        [onChangeRowType, onFlipRowSign, onSetDocumentTargetRow],
    );

    const getRowStyle = useCallback(
        (params: { data?: TAllRowsAvailable }) => {
            const row = params.data;
            if (!row) return { backgroundColor: "none" };

            const isOmitted = isRowOmitted(row);
            const isNonOperatingAccount =
                isRowWithOperatingStatements(row) &&
                accountsNonOperating.some(
                    option => option.value === row.account,
                );
            const shouldRowBeGreyedOut = isOmitted || isNonOperatingAccount;
            const isTargetRow = highlightedSet.has(row.rowId);

            if (isTargetRow) {
                return {
                    backgroundColor: "var(--proda-yellow-20)",
                };
            }
            return {
                backgroundColor: shouldRowBeGreyedOut
                    ? "var(--proda-white-110)"
                    : "var(--proda-white)",
            };
        },
        [accountsNonOperating, highlightedSet],
    );

    const onCellClicked = useCallback<
        NonNullable<GridMappingsProps["onCellClicked"]>
    >(
        event => {
            const data = event.data;
            const editedColumn = columnDefsOperatingStatementsFromProps.find(
                column => column.field === event.colDef.field,
            );
            if (
                !onValueCellSelected ||
                !data ||
                data.rowType !== "account" ||
                editedColumn?.columnId == null ||
                editedColumn.isDisabled === true ||
                event.colDef.field == null
            ) {
                onValueCellSelected?.(null);
                return;
            }
            onValueCellSelected({
                rowId: Number(data.rowId),
                columnId: editedColumn.columnId,
                field: event.colDef.field,
            });
        },
        [columnDefsOperatingStatementsFromProps, onValueCellSelected],
    );

    const onPasteStart = useCallback(() => {
        isPasting.current = true;
        pasteBuffer.current = [];
        valuePasteBuffer.current = [];
    }, []);

    const onPasteEnd = useCallback(() => {
        isPasting.current = false;
        const changes = pasteBuffer.current;
        pasteBuffer.current = [];
        if (changes.length > 0) {
            onChangeAccounts(changes);
        }
        const valueChanges = valuePasteBuffer.current;
        valuePasteBuffer.current = [];
        if (valueChanges.length > 0) {
            onCellValuesEdited(valueChanges);
        }
    }, [onCellValuesEdited, onChangeAccounts]);

    const onCellEditRequest = useCallback(
        (event: CellEditRequestEvent<TAllRowsAvailable>) => {
            const editedColumn = columnDefsOperatingStatementsFromProps.find(
                column => column.field === event.colDef.field,
            );
            if (editedColumn !== undefined) {
                if (
                    !allowValueEditing ||
                    editedColumn.columnId == null ||
                    editedColumn.isDisabled === true ||
                    event.data.rowType !== "account"
                ) {
                    return;
                }
                const columnId = editedColumn.columnId;
                // Editor uses dollars; row data uses cents.
                const amount = parseEditedDollars(event.newValue);
                if (amount === undefined) return;
                const rowId = Number(event.data.rowId);
                const oldValue = event.oldValue as number | null;
                const toDollars = (v: number | null) =>
                    v == null ? null : v / 100;

                if (isPasting.current) {
                    if (amount !== toDollars(oldValue)) {
                        valuePasteBuffer.current = [
                            ...valuePasteBuffer.current,
                            { rowId, columnId, amount },
                        ];
                    }
                    return;
                }

                const selected = getValueCellSelectionForEdit({
                    api: event.api,
                    rowId,
                    columnId,
                });
                const targets =
                    selected.length > 0
                        ? selected
                        : [{ rowId, columnId, current: oldValue }];
                const changed = targets.filter(
                    cell => toDollars(cell.current) !== amount,
                );
                if (changed.length === 0) return;
                onCellValuesEdited(
                    changed.map(cell => ({
                        rowId: cell.rowId,
                        columnId: cell.columnId,
                        amount,
                    })),
                );
                return;
            }

            if (event.colDef.field !== "account") return;

            const oldValue = event.oldValue as string;
            const newValue = event.newValue as string;

            if (oldValue === newValue) return;

            const rowId = Number(event.data.rowId);
            const isOmit = isOmitOption({ value: newValue });
            const change = {
                rowId,
                accountCode: isOmit ? null : newValue,
            };

            if (isPasting.current) {
                const isKnownAccount = optionsAccounts.some(
                    opt => opt.value === newValue,
                );
                if (!isOmit && !isKnownAccount) return;
                pasteBuffer.current = [...pasteBuffer.current, change];
                return;
            }

            onChangeAccounts([change]);
        },
        [
            columnDefsOperatingStatementsFromProps,
            allowValueEditing,
            getValueCellSelectionForEdit,
            onCellValuesEdited,
            onChangeAccounts,
            optionsAccounts,
        ],
    );

    const onCellKeyDown = useCallback<
        NonNullable<GridMappingsProps["onCellKeyDown"]>
    >(event => {
        if (isCellKeyDownEventTypeGuard(event)) {
            const keyboardEvent = event.event as KeyboardEvent;
            const isAltDown = keyboardEvent.altKey;
            const isDownKey = keyboardEvent.key === "ArrowDown";

            if (
                isAltDown &&
                isDownKey &&
                event.colDef.field === "account" &&
                event.rowIndex !== null
            ) {
                event.api.startEditingCell({
                    rowIndex: event.rowIndex,
                    colKey: event.column.getColId(),
                });
            }
        }
    }, []);

    const onSelectionChanged = useCallback(
        (params: SelectionChangedEvent<TAllRowsAvailable>) => {
            const selectedRowIds = params.api
                .getSelectedNodes()
                .map(node => node.data?.rowId)
                .filter((rowId): rowId is number => typeof rowId === "number");

            setSelectedRows?.(selectedRowIds);
        },
        [setSelectedRows],
    );

    return (
        <Grid<TAllRowsAvailable>
            className={clsx([testId])}
            getContextMenuItems={getContextMenuItems}
            gridTheme="ag-theme-custom-proda"
            sx={{
                ...stylesToForceFullWidthOfRichEditSelectDropdownsItems,
                ...disableRowAnimationOnZooming,
            }}
            rowData={rowDataWithTotalRows}
            pinnedBottomRowData={pinnedBottomRowData}
            columnDefs={columnDefs}
            gridOptions={gridOptions}
            getRowId={getRowId}
            defaultColDef={defaultColDef}
            readOnlyEdit={true}
            enterNavigatesVertically={true}
            enterNavigatesVerticallyAfterEdit={true}
            suppressHeaderFocus={true}
            // This grid needs row and cell selection together.
            enableRangeSelection={true}
            enableBrowserTooltips={true}
            suppressMovableColumns={true}
            focusGridInnerElement={focusFirstCellInAccountColumn}
            getRowStyle={getRowStyle}
            onStateUpdated={onStateUpdated}
            onCellClicked={onCellClicked}
            onPasteStart={onPasteStart}
            onPasteEnd={onPasteEnd}
            onCellEditRequest={onCellEditRequest}
            onCellKeyDown={onCellKeyDown}
            onRangeSelectionChanged={onRangeSelectionChanged}
            processCellForClipboard={processCellForClipboard}
            processCellFromClipboard={processCellFromClipboard}
            onSelectionChanged={onSelectionChanged}
            {...otherProps}
        />
    );
});

GridMappings.displayName = "GridMappings";

export default GridMappings;
